/**
 * TimezonePicker
 *
 * Top-nav dropdown that controls the application-wide timezone used for both
 * filtering (date inputs sent to the backend) and display (dayjs.tz formatting).
 *
 * The user sees friendly city/country labels with a current GMT offset; the
 * underlying IANA identifier (e.g. `America/New_York`) is kept as a secondary
 * hint for power users but isn't the primary text.
 */

import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Button as MuiButton,
    Menu,
    MenuItem,
    Typography,
    TextField,
    InputAdornment,
    Divider,
} from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { setTimezone } from '@features/redux/slices/timezoneSlice';
import { getDetectedTz } from '@utils/tzRegistry';
import { colors, typography as typographyTokens, radii } from '@components/design-system/tokens';

import type { RootState } from '@features/redux/store';

/**
 * Friendly labels for the most common picks. Keys are IANA identifiers; values
 * are { city, region } pairs shown to the user. Anything not in this map falls
 * back to a derived label from the IANA path itself (e.g. `Asia/Kuala_Lumpur`
 * → "Kuala Lumpur").
 */
const FRIENDLY_LABELS: Record<string, { city: string; region: string }> = {
    'UTC': { city: 'UTC', region: 'Coordinated Universal Time' },
    'America/New_York': { city: 'New York', region: 'Eastern (US & Canada)' },
    'America/Chicago': { city: 'Chicago', region: 'Central (US & Canada)' },
    'America/Denver': { city: 'Denver', region: 'Mountain (US & Canada)' },
    'America/Los_Angeles': { city: 'Los Angeles', region: 'Pacific (US & Canada)' },
    'America/Anchorage': { city: 'Anchorage', region: 'Alaska' },
    'Pacific/Honolulu': { city: 'Honolulu', region: 'Hawaii' },
    'America/Toronto': { city: 'Toronto', region: 'Eastern (Canada)' },
    'America/Vancouver': { city: 'Vancouver', region: 'Pacific (Canada)' },
    'America/Mexico_City': { city: 'Mexico City', region: 'Mexico' },
    'America/Sao_Paulo': { city: 'São Paulo', region: 'Brazil' },
    'Europe/London': { city: 'London', region: 'United Kingdom' },
    'Europe/Dublin': { city: 'Dublin', region: 'Ireland' },
    'Europe/Paris': { city: 'Paris', region: 'France' },
    'Europe/Berlin': { city: 'Berlin', region: 'Germany' },
    'Europe/Madrid': { city: 'Madrid', region: 'Spain' },
    'Europe/Amsterdam': { city: 'Amsterdam', region: 'Netherlands' },
    'Europe/Rome': { city: 'Rome', region: 'Italy' },
    'Europe/Athens': { city: 'Athens', region: 'Greece' },
    'Europe/Istanbul': { city: 'Istanbul', region: 'Türkiye' },
    'Asia/Dubai': { city: 'Dubai', region: 'UAE' },
    'Asia/Kolkata': { city: 'Mumbai', region: 'India' },
    'Asia/Bangkok': { city: 'Bangkok', region: 'Thailand' },
    'Asia/Singapore': { city: 'Singapore', region: 'Singapore' },
    'Asia/Hong_Kong': { city: 'Hong Kong', region: 'Hong Kong' },
    'Asia/Shanghai': { city: 'Shanghai', region: 'China' },
    'Asia/Tokyo': { city: 'Tokyo', region: 'Japan' },
    'Asia/Seoul': { city: 'Seoul', region: 'South Korea' },
    'Australia/Perth': { city: 'Perth', region: 'Western Australia' },
    'Australia/Sydney': { city: 'Sydney', region: 'Eastern Australia' },
    'Pacific/Auckland': { city: 'Auckland', region: 'New Zealand' },
};

const COMMON_ZONES: string[] = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'America/Toronto',
    'America/Vancouver',
    'America/Mexico_City',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Madrid',
    'Europe/Amsterdam',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Dubai',
    'Australia/Sydney',
    'UTC',
];

const supportedZones = (): string[] => {
    const intlAny = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    if (typeof intlAny.supportedValuesOf === 'function') {
        try {
            return intlAny.supportedValuesOf('timeZone');
        } catch {
            /* fall through */
        }
    }
    return [...COMMON_ZONES];
};

/**
 * "America/New_York" → { city: "New York", region: "America" }
 * "Asia/Kuala_Lumpur" → { city: "Kuala Lumpur", region: "Asia" }
 * Bare "UTC" stays as-is.
 */
const labelFor = (tz: string): { city: string; region: string } => {
    if (FRIENDLY_LABELS[tz]) return FRIENDLY_LABELS[tz];
    const segments = tz.split('/');
    const tail = segments[segments.length - 1] ?? tz;
    const head = segments.length > 1 ? segments[0] : '';
    return {
        city: tail.replace(/_/g, ' '),
        region: head.replace(/_/g, ' '),
    };
};

const formatOffset = (tz: string): string => {
    try {
        const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' });
        const offsetPart = formatter.formatToParts(new Date()).find((part) => part.type === 'timeZoneName');
        // Normalize "GMT-5" → "GMT−5" (proper minus sign, easier to read)
        return offsetPart?.value.replace('-', '−') ?? '';
    } catch {
        return '';
    }
};

const matchesSearch = (tz: string, term: string): boolean => {
    if (!term) return true;
    const lowered = term.toLowerCase();
    if (tz.toLowerCase().includes(lowered)) return true;
    const { city, region } = labelFor(tz);
    return city.toLowerCase().includes(lowered) || region.toLowerCase().includes(lowered);
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        sx={{
            px: 2,
            py: 1,
            fontSize: typographyTokens.fontSize.xs,
            fontWeight: typographyTokens.fontWeight.semibold,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.neutral[400],
        }}>
        {children}
    </Typography>
);

const ZoneRow: React.FC<{ tz: string; selected: boolean; onClick: () => void }> = ({ tz, selected, onClick }) => {
    const { city, region } = labelFor(tz);
    return (
        <MenuItem
            onClick={onClick}
            selected={selected}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
                px: 2,
            }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    sx={{
                        fontSize: typographyTokens.fontSize.sm,
                        fontWeight: typographyTokens.fontWeight.medium,
                        color: colors.neutral[900],
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                    {city}
                </Typography>
                {region && (
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[500],
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {region}
                    </Typography>
                )}
            </Box>
            <Typography
                sx={{
                    fontSize: typographyTokens.fontSize.xs,
                    fontWeight: typographyTokens.fontWeight.medium,
                    color: colors.neutral[600],
                    fontVariantNumeric: 'tabular-nums',
                    flexShrink: 0,
                }}>
                {formatOffset(tz)}
            </Typography>
        </MenuItem>
    );
};

export const TimezonePicker: React.FC = () => {
    const dispatch = useDispatch();
    const tz = useSelector((state: RootState) => state.timezone.tz);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [search, setSearch] = useState('');

    const detected = useMemo(() => getDetectedTz(), []);
    const allZones = useMemo(() => supportedZones(), []);

    const triggerLabel = labelFor(tz).city;
    const triggerOffset = formatOffset(tz);

    // Sections shown when the user isn't searching
    const pinnedSections = useMemo(() => {
        const yourZone = detected && allZones.includes(detected) ? detected : null;
        const commonOnly = COMMON_ZONES.filter((zone) => zone !== yourZone);
        return { yourZone, common: commonOnly };
    }, [detected, allZones]);

    const filtered = useMemo(() => {
        const term = search.trim();
        if (!term) return null;
        return allZones.filter((zone) => matchesSearch(zone, term)).slice(0, 50);
    }, [allZones, search]);

    const handleSelect = (next: string) => {
        dispatch(setTimezone(next));
        setAnchor(null);
        setSearch('');
    };

    return (
        <>
            <MuiButton
                onClick={(event) => setAnchor(event.currentTarget)}
                startIcon={<PublicOutlinedIcon />}
                size='small'
                sx={{
                    color: colors.neutral[700],
                    textTransform: 'none',
                    fontWeight: typographyTokens.fontWeight.medium,
                    fontSize: typographyTokens.fontSize.sm,
                    borderRadius: `${radii.md}px`,
                    px: 1.25,
                    minWidth: 0,
                    gap: 0.75,
                    '&:hover': { backgroundColor: colors.neutral[100] },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box component='span'>{triggerLabel}</Box>
                    {triggerOffset && (
                        <Box
                            component='span'
                            sx={{
                                fontSize: typographyTokens.fontSize.xs,
                                color: colors.neutral[500],
                                fontVariantNumeric: 'tabular-nums',
                            }}>
                            {triggerOffset}
                        </Box>
                    )}
                </Box>
            </MuiButton>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                PaperProps={{ sx: { width: 340, maxHeight: 480 } }}>
                <Box sx={{ p: 1.25, pt: 1 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        size='small'
                        placeholder='Search by city or country…'
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon sx={{ fontSize: 18, color: colors.neutral[400] }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {filtered ? (
                    filtered.length === 0 ? (
                        <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: typographyTokens.fontSize.sm, color: colors.neutral[600] }}>
                                No timezones match "{search.trim()}"
                            </Typography>
                            <Typography sx={{ fontSize: typographyTokens.fontSize.xs, color: colors.neutral[400], mt: 0.5 }}>
                                Try a city or country name.
                            </Typography>
                        </Box>
                    ) : (
                        filtered.map((zone) => (
                            <ZoneRow
                                key={zone}
                                tz={zone}
                                selected={zone === tz}
                                onClick={() => handleSelect(zone)}
                            />
                        ))
                    )
                ) : (
                    <>
                        {pinnedSections.yourZone && (
                            <>
                                <SectionLabel>
                                    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocationOnOutlinedIcon sx={{ fontSize: 12 }} />
                                        Your location
                                    </Box>
                                </SectionLabel>
                                <ZoneRow
                                    tz={pinnedSections.yourZone}
                                    selected={pinnedSections.yourZone === tz}
                                    onClick={() => handleSelect(pinnedSections.yourZone!)}
                                />
                                <Divider sx={{ my: 0.5 }} />
                            </>
                        )}

                        <SectionLabel>Common timezones</SectionLabel>
                        {pinnedSections.common.map((zone) => (
                            <ZoneRow
                                key={zone}
                                tz={zone}
                                selected={zone === tz}
                                onClick={() => handleSelect(zone)}
                            />
                        ))}

                        <Divider sx={{ my: 0.5 }} />
                        <Typography
                            sx={{
                                px: 2,
                                py: 1,
                                fontSize: typographyTokens.fontSize.xs,
                                color: colors.neutral[500],
                                textAlign: 'center',
                            }}>
                            Search above to see all timezones
                        </Typography>
                    </>
                )}
            </Menu>
        </>
    );
};

export default TimezonePicker;
